import { useState } from 'react'
import { Plus, X } from 'lucide-react'
import { toast } from 'sonner'
import { useContentReadOnly } from '@/hooks/useConfig'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useCommunityHiddenEnterprises,
  useUpdateCommunityHiddenEnterprises,
  useOutageNotice,
  useUpdateOutageNotice,
  type CommunityHiddenEntry,
  type OutageNotice,
  type SsoSource,
} from '../api/admin-api'

const entryKey = (entry: CommunityHiddenEntry) => `${entry.source}:${entry.enterpriseId}`

const entriesEqual = (a: CommunityHiddenEntry[], b: CommunityHiddenEntry[]) => {
  if (a.length !== b.length) return false
  return a.every((entry, idx) => entry.source === b[idx].source && entry.enterpriseId === b[idx].enterpriseId)
}

const outageNoticeEqual = (a: OutageNotice, b: OutageNotice) =>
  a.enabled === b.enabled && a.message === b.message

export function SettingsPage() {
  const { data, isLoading, error } = useCommunityHiddenEnterprises()
  const updateMutation = useUpdateCommunityHiddenEnterprises()
  const readOnly = useContentReadOnly()

  const {
    data: outageData,
    isLoading: outageLoading,
    error: outageError,
  } = useOutageNotice()
  const updateOutageMutation = useUpdateOutageNotice()

  // null = no local edits, mirrors server state
  const [draftOverride, setDraftOverride] = useState<CommunityHiddenEntry[] | null>(null)
  const [sourceInput, setSourceInput] = useState<SsoSource>('wrks')
  const [inputValue, setInputValue] = useState('')

  const [outageDraft, setOutageDraft] = useState<OutageNotice | null>(null)

  const serverEntries = data?.entries ?? []
  const draft = draftOverride ?? serverEntries
  const setDraft = (next: CommunityHiddenEntry[]) => setDraftOverride(next)

  const hasChanges = draftOverride != null && !entriesEqual(draftOverride, serverEntries)

  const outageServer = outageData ?? null
  const outageCurrent: OutageNotice =
    outageDraft ?? outageServer ?? { enabled: false, message: '' }
  const outageHasChanges =
    outageDraft != null && outageServer != null && !outageNoticeEqual(outageDraft, outageServer)

  const handleAdd = () => {
    const trimmed = inputValue.trim()
    if (!trimmed) return
    const parsed = Number.parseInt(trimmed, 10)
    if (!Number.isFinite(parsed) || parsed <= 0 || String(parsed) !== trimmed) {
      toast.error('양의 정수 enterprise ID만 입력 가능합니다')
      return
    }
    const candidate: CommunityHiddenEntry = { source: sourceInput, enterpriseId: parsed }
    if (draft.some((entry) => entryKey(entry) === entryKey(candidate))) {
      toast.error('이미 추가된 조합입니다')
      return
    }
    setDraft([...draft, candidate])
    setInputValue('')
  }

  const handleRemove = (target: CommunityHiddenEntry) => {
    setDraft(draft.filter((entry) => entryKey(entry) !== entryKey(target)))
  }

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync(draft)
      setDraftOverride(null)
      toast.success('설정이 저장되었습니다')
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : '저장 중 오류가 발생했습니다')
    }
  }

  const handleReset = () => {
    setDraftOverride(null)
  }

  const handleOutageSave = async () => {
    const trimmedMessage = outageCurrent.message.trim()
    if (!trimmedMessage) {
      toast.error('공지 메시지를 입력해주세요')
      return
    }
    try {
      await updateOutageMutation.mutateAsync({
        enabled: outageCurrent.enabled,
        message: trimmedMessage,
      })
      setOutageDraft(null)
      toast.success('장애 공지가 저장되었습니다')
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : '저장 중 오류가 발생했습니다')
    }
  }

  const handleOutageReset = () => {
    setOutageDraft(null)
  }

  if (isLoading || outageLoading)
    return <div className="text-center py-12 text-gray-500">로딩 중...</div>
  if (error || outageError)
    return (
      <div className="text-center py-12 text-red-500">
        에러: {(error ?? outageError)?.message}
      </div>
    )

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-heading tracking-tight">설정</h1>
        <p className="text-caption mt-2 leading-relaxed">
          앱 전역 설정을 관리합니다
        </p>
      </div>

      <section className="bg-white rounded-lg border p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-heading mb-2 tracking-tight">
            장애 공지
          </h2>
          <p className="text-sm text-caption leading-relaxed">
            활성화하면 모든 사용자에게 경고 배너가 노출되고 비디오 생성 요청이 차단됩니다.
            메시지는 줄바꿈을 포함할 수 있습니다.
          </p>
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1">
            <Label htmlFor="outage-enabled" className="text-sm font-medium text-gray-700">
              공지 활성화
            </Label>
            <p className="text-xs text-caption">
              켜면 즉시 전체 사용자에게 적용됩니다.
            </p>
          </div>
          <Switch
            id="outage-enabled"
            checked={outageCurrent.enabled}
            onCheckedChange={(checked) =>
              setOutageDraft({
                enabled: checked,
                message: outageCurrent.message,
              })
            }
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="outage-message">공지 메시지</Label>
          <Textarea
            id="outage-message"
            rows={5}
            value={outageCurrent.message}
            placeholder={'예) 2026-05-27 18:00~20:00 점검\n외부 API 장애로 비디오 생성을 일시 중단합니다.'}
            onChange={(e) =>
              setOutageDraft({
                enabled: outageCurrent.enabled,
                message: e.target.value,
              })
            }
          />
        </div>

        <div className="flex items-center gap-2 pt-2 border-t">
          <Button
            type="button"
            onClick={handleOutageSave}
            disabled={!outageHasChanges || updateOutageMutation.isPending}
          >
            {updateOutageMutation.isPending ? '저장 중...' : '저장'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleOutageReset}
            disabled={!outageHasChanges || updateOutageMutation.isPending}
          >
            되돌리기
          </Button>
        </div>
      </section>

      <section className="bg-white rounded-lg border p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-heading mb-2 tracking-tight">
            커뮤니티 메뉴 숨김
          </h2>
          <p className="text-sm text-caption leading-relaxed">
            여기에 등록된 source + enterprise ID 조합에 속한 사용자는 커뮤니티 메뉴가 노출되지 않으며,
            커뮤니티 비디오 조회/공유 API가 차단됩니다. enterprise ID는 source마다 1부터 시작하므로
            반드시 source와 함께 등록해야 합니다.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="enterprise-id-input">조합 추가</Label>
          <div className="flex gap-2">
            <Select value={sourceInput} onValueChange={(v) => setSourceInput(v as SsoSource)}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="wrks">wrks</SelectItem>
                <SelectItem value="gov">gov</SelectItem>
              </SelectContent>
            </Select>
            <Input
              id="enterprise-id-input"
              type="text"
              inputMode="numeric"
              placeholder="예: 12345"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAdd()
                }
              }}
            />
            <Button type="button" onClick={handleAdd}>
              <Plus className="w-4 h-4 mr-1" />
              추가
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <Label>등록된 조합</Label>
          {draft.length === 0 ? (
            <div className="text-sm text-caption py-3">아직 등록된 항목이 없습니다.</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {draft.map((entry) => (
                <div
                  key={entryKey(entry)}
                  className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 bg-gray-100 rounded-full text-sm text-body"
                >
                  <span className="font-mono">
                    <span className="text-caption">{entry.source}</span>{' '}
                    {entry.enterpriseId}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemove(entry)}
                    className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-gray-200 text-caption hover:text-body transition-colors"
                    aria-label={`${entry.source} ${entry.enterpriseId} 삭제`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 pt-2 border-t">
          <Button
            type="button"
            onClick={handleSave}
            disabled={readOnly || !hasChanges || updateMutation.isPending}
          >
            {updateMutation.isPending ? '저장 중...' : '저장'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            disabled={!hasChanges || updateMutation.isPending}
          >
            되돌리기
          </Button>
        </div>
      </section>
    </div>
  )
}
